'use client';

import React from 'react';

import styled from '@emotion/styled';
import { formatFullDate } from '../../../../i18n/formatFullDate';
import { Css, Markdown, Responsive, ContentWrap } from '../../../../ui';
import { IPageLayoutProps } from '../../../shared/layout/page/PageLayout';
import { IPageEntry } from '../../../../contentful/types/IPage';

import { MarkdownRenderContextProvider } from '../../../../components/common/markdown';
import GenericPage from '../../../app/pages/ui/GenericPage';
import { IPressReleaseEntry } from '../../../../contentful';
import { PageRoutes } from '../../../app/pages/domain/constants/PageRoutes';
import PressReleaseContactSection from './PressReleaseContactSection';
import PressReleaseDownloadsSection from './PressReleaseDownloadsSection';
import { SocialLinks } from '../../../../components/common/social-links/SocialLinks';
import { useGetCurrentUrl } from '../../../../utils/useGetCurrentUrl';
import { ISiteSectionEntry } from '../../../../contentful/types/ISiteSection';

export interface IProps extends IPageLayoutProps {
	page: IPageEntry;
	pressRelease: IPressReleaseEntry;
	siteSection?: ISiteSectionEntry;
}

export default function PressReleasePage({
	page,
	pressRelease,
	siteSection,
}: IProps) {
	const { title, body, publicationDate, downloadableFiles } =
		pressRelease.fields;

	const currentUrl = useGetCurrentUrl();

	return (
		<GenericPage
			title={title}
			backlink={PageRoutes.CorporateNewsroomPressReleases}
			backlinkText="Press Releases"
			page={page}
			hideHeaderImage
			siteSection={siteSection}
		>
			<ContentWrap>
				<Wrapper>
					{publicationDate && (
						<PublicationDate>
							{formatFullDate(
								publicationDate || pressRelease.updatedAt,
								'MMM d, yyyy',
							)}
						</PublicationDate>
					)}
					<MarkdownRenderContextProvider>
						<Markdown source={body} />
					</MarkdownRenderContextProvider>

					<SocialLinks variant="dark" shareLinkUrl={currentUrl} />
				</Wrapper>

				<PressReleaseContactSection />

				{downloadableFiles?.length > 0 && (
					<PressReleaseDownloadsSection assets={downloadableFiles} />
				)}
			</ContentWrap>
		</GenericPage>
	);
}

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	margin-bottom: ${Css.pxToRem(40)};

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		h5 {
			max-width: 792px;
		}
	}
`;

const PublicationDate = styled.p`
	margin-bottom: ${Css.pxToRem(24)};
`;
